
import React from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, User, Calendar, CreditCard, FileText, Activity, MapPin, Phone, Mail } from 'lucide-react';
import PatientDetailOutlet from '@/components/PatientDetailOutlet';

const PatientDetail = () => {
  const { id } = useParams();
  const location = useLocation();

  // Mock patient data
  const patient = {
    id: id,
    name: 'Sarah Johnson',
    email: 'sarah.j@email.com',
    phone: '(555) 123-4567',
    status: 'active',
    joinDate: '2023-06-15',
    lastVisit: '2024-01-15',
    creditBalance: 450,
    totalVisits: 8,
    address: '123 Main St, Austin, TX 78701',
    emergencyContact: 'John Johnson - (555) 987-6543',
    notes: 'Prefers morning appointments. Allergic to latex.'
  };

  const getCurrentTab = () => {
    const path = location.pathname.split('/').pop();
    switch (path) {
      case 'appointments': return 'appointments';
      case 'credits': return 'credits'; 
      case 'treatments': return 'treatments';
      case 'notes': return 'notes';
      case 'ledger': return 'ledger';
      default: return 'appointments';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <Button asChild variant="outline">
              <Link to="/staff/patients">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Patients
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                {patient.name}
              </h1>
              <p className="text-juv-deep-chestnut/70">Patient Profile</p>
            </div>
          </div>

          {/* Patient Info Card */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <User className="h-5 w-5 text-juv-terracotta" />
                    <div>
                      <p className="font-medium text-juv-deep-chestnut">{patient.name}</p>
                      <Badge className={patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {patient.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{patient.email}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{patient.phone}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-5 w-5 text-juv-terracotta" />
                    <div>
                      <p className="font-medium text-juv-deep-chestnut">Credit Balance</p>
                      <p className="text-lg font-bold">${patient.creditBalance}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Last Visit</p>
                      <p className="text-sm">{new Date(patient.lastVisit).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Activity className="h-4 w-4 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Total Visits</p>
                      <p className="text-sm">{patient.totalVisits}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-2">
                    <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="text-sm">{patient.address}</p>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Emergency Contact</p>
                    <p className="text-sm">{patient.emergencyContact}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs Navigation */}
          <Tabs value={getCurrentTab()} className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="appointments" asChild>
                <Link to={`/staff/patients/${id}/appointments`}>Appointments</Link>
              </TabsTrigger>
              <TabsTrigger value="credits" asChild>
                <Link to={`/staff/patients/${id}/credits`}>Credits</Link>
              </TabsTrigger>
              <TabsTrigger value="treatments" asChild>
                <Link to={`/staff/patients/${id}/treatments`}>Treatments</Link>
              </TabsTrigger>
              <TabsTrigger value="notes" asChild>
                <Link to={`/staff/patients/${id}/notes`}>Notes</Link>
              </TabsTrigger>
              <TabsTrigger value="ledger" asChild>
                <Link to={`/staff/patients/${id}/ledger`}>Ledger</Link>
              </TabsTrigger>
            </TabsList>

            <TabsContent value={getCurrentTab()}>
              <PatientDetailOutlet />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;
